/**
 * Boîte de confirmation modale.
 *
 * `confirmPhrase` active la double confirmation exigée pour la suppression
 * d'une application par un administrateur (§6.3) : le bouton ne s'active que
 * si la phrase exacte est ressaisie.
 */

import { X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { Button, Label, TextInput } from './ui';

/** Propriétés de la boîte de dialogue. */
export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: ReactNode;
  /** Phrase à ressaisir pour débloquer la confirmation. */
  confirmPhrase?: string;
  confirmLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  /** Contenu additionnel (motif de la demande, options…). */
  children?: ReactNode;
}

/** Modale de confirmation. */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmPhrase,
  confirmLabel = 'Confirmer',
  destructive = false,
  busy = false,
  error,
  onConfirm,
  onCancel,
  children,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  const inputId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  // Le champ est vidé à chaque ouverture : une confirmation ne se pré-remplit
  // pas, c'est tout l'intérêt de la double saisie.
  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  // Échap ferme la modale.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onCancel]);

  // Le focus entre dans la modale à l'ouverture.
  useEffect(() => {
    if (open) dialogRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const blocked = confirmPhrase !== undefined && typed !== confirmPhrase;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="w-full max-w-lg rounded-lg border border-slate-200 bg-white p-6 shadow-xl outline-none dark:border-slate-700 dark:bg-slate-900"
      >
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-50">{title}</h2>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Fermer"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="mt-3 text-sm text-slate-700 dark:text-slate-300">{description}</div>

        {children ? <div className="mt-4">{children}</div> : null}

        {confirmPhrase !== undefined ? (
          <div className="mt-4">
            <Label htmlFor={inputId} required>
              Saisissez « {confirmPhrase} » pour confirmer
            </Label>
            <TextInput
              id={inputId}
              value={typed}
              autoComplete="off"
              spellCheck={false}
              onChange={(event) => setTyped(event.target.value)}
              className="font-mono"
            />
          </div>
        ) : null}

        {error ? (
          <p role="alert" className="mt-3 text-sm text-red-700 dark:text-red-400">
            {error}
          </p>
        ) : null}

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Annuler
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={blocked}
            loading={busy}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
